// eslint-disable-next-line no-restricted-imports
import {Avatar as DIGAvatar, AvatarSizes} from '@dropbox/dig-components/avatar';
import {Text} from '@dropbox/dig-components/typography';
import {Atoms, atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon, UIIconSize} from '@dropbox/dig-icons';
import {PulseUser} from 'atoms/auth';
import cx from 'classnames';
import {EmployeeLdap, EmployeeRoleLevel} from 'client';
import {t} from 'i18next';
import {forwardRef, useEffect, useState} from 'react';
import {getBackendUrl, getProfileImageUrl, nameToInitials} from 'utilities';

import styles from './Avatar.module.css';

type DisplayNameUser = {
  display_name: string;
  abbreviated_name?: string;
  profile_photo_url?: string | null;
  email?: string;
};

type DeactivationAwareUser = {
  is_deactivated?: boolean | null;
};

type ProfileAvatarProps = {
  className?: string;
  style?: React.CSSProperties;
  size?: AvatarSizes | 'xlarge' | 'profile' | 'edit';
  onLeave?: boolean;
  isInactive?: boolean;
  user:
    | {
        name: string;
        ldap?: string;
        profile_photo_url?: string | null;
        is_deactivated?: boolean | null;
      }
    | DisplayNameUser
    | EmployeeLdap
    | EmployeeRoleLevel
    | PulseUser;
};

/**
 * Module-level cache of photo URLs that have already loaded, along with their
 * computed crop transform. Avatars frequently remount with the same `src`
 * (e.g. person chips inside a Markdown answer that re-parses on every streamed
 * token). Without this, each remount resets `imageLoaded` to `false` and
 * replays the fade-in animation, which reads as a flicker. Seeding state from
 * this cache lets a remounted avatar render its already-cached image instantly.
 */
const loadedPhotoTransforms = new Map<string, number>();

const userHasDisplayName = (user: any): user is DisplayNameUser | PulseUser => {
  return typeof user !== 'string' && 'display_name' in user;
};

const userIsDeactivated = (user: ProfileAvatarProps['user']) => {
  return typeof user !== 'string' && Boolean((user as DeactivationAwareUser).is_deactivated);
};

/**
 * Avatar component for displaying user a profile photo or initials
 *
 * @param size - size of the avatar
 * @param user - Employee | GoalUser | PulseUser | ldap
 * @param onLeave - whether the user is on leave
 */
export const Avatar = forwardRef<HTMLElement, ProfileAvatarProps>(
  ({size, user, onLeave, isInactive, className, style}, ref: any) => {
    const isAvatarInactive = Boolean(isInactive || userIsDeactivated(user));

    const url =
      typeof user === 'string'
        ? getProfileImageUrl(user)
        : ((user as DisplayNameUser).profile_photo_url ??
          getProfileImageUrl((user as EmployeeLdap).ldap));

    const isCustomSize = ['xlarge', 'profile', 'edit'].includes(size ?? '');

    const photoURL = url?.includes('https://') ? url : getBackendUrl() + url;

    // Seed from the module-level cache so a remount of an already-loaded photo
    // renders immediately instead of replaying the fade-in (avoids flicker).
    const [imageLoaded, setImageLoaded] = useState(() => loadedPhotoTransforms.has(photoURL));
    const [transform, setTransform] = useState(() => loadedPhotoTransforms.get(photoURL) ?? 0);
    const employeeName = userHasDisplayName(user)
      ? user.display_name
      : typeof user === 'string'
        ? user
        : user.name;
    const employeeInitials =
      userHasDisplayName(user) && user.abbreviated_name
        ? user.abbreviated_name
        : nameToInitials(employeeName);

    // Hacky workaround until we allow people to adjust their crop
    useEffect(() => {
      const image = new Image();
      image.src = photoURL;

      const onLoad = () => {
        setImageLoaded(true);

        const width = image.naturalWidth;
        const height = image.naturalHeight;

        let nextTransform = 0;
        if (height === width) {
          // Square thumbnail from editor
          nextTransform = 0;
        } else if (height === 300 && width === 200) {
          // This seems to be a fairly common dimension
          nextTransform = 1;
        } else if (height > width) {
          // otherwise they're ~195 x ~315 — ish.
          nextTransform = 2;
        }
        setTransform(nextTransform);
        loadedPhotoTransforms.set(photoURL, nextTransform);
      };
      image.addEventListener('load', onLoad);
      return () => {
        image.removeEventListener('load', onLoad);
      };
    }, [photoURL]);

    if (onLeave && size !== 'profile') {
      throw new Error(
        'DSYS/Avatar.tsx: `onLeave` prop is only supported for `profile` size avatars'
      );
    }

    return (
      <Box
        position="relative"
        ref={ref}
        className={cx(className, {
          [styles.avatar64]: size === 'xlarge',
          [styles.avatar128]: size === 'profile',
          [styles.avatar211]: size === 'edit',
        })}
        style={style}
      >
        {onLeave && !isAvatarInactive && (
          <div className={styles.wrapper}>
            <div className={styles.ticker}>
              <div className={styles.tickerText}>
                <Text size="small">{t('currently_on_leave')}</Text>
              </div>
            </div>
          </div>
        )}
        <DIGAvatar
          key={photoURL}
          src={photoURL}
          hasNoOutline
          size={isCustomSize ? 'large' : (size as AvatarSizes)}
          alt={employeeName}
          isInactive={isAvatarInactive}
          className={cx(
            styles.avatar,
            {
              [styles.avatarLoaded]: imageLoaded,
              [styles.deactivatedAvatarLoaded]: isAvatarInactive,
              [styles.moveUpXSmall]: transform === 1 && !isCustomSize,
              [styles.moveUpSmall]: transform === 2 && !isCustomSize,
              [styles.moveUpMedium]: transform === 2 && size === 'xlarge',
              [styles.moveUpBig]: transform === 2 && size === 'profile',
            },
            atoms({backgroundColor: 'Opacity Surface'})
          )}
        >
          {employeeInitials}
        </DIGAvatar>
      </Box>
    );
  }
);

Avatar.displayName = 'Avatar';

export const AvatarIcon = ({
  src,
  size = 'medium',
  background = 'var(--dig-color__opacity__surface)',
  color = 'Text Subtle',
  className,
  iconSize,
}: {
  src: React.ComponentType<React.SVGAttributes<SVGElement>>;
  size?: UIIconSize;
  background?: string;
  color?: Atoms['color'];
  className?: string;
  iconSize?: UIIconSize;
}) => (
  <Box className={className}>
    <DIGAvatar colorPalette={{background, foreground: ''}} size={size}>
      <UIIcon size={iconSize ? iconSize : size} src={src} className={atoms({color: color})} />
    </DIGAvatar>
  </Box>
);
